import { t } from "@lingui/core/macro";
import Logger from "@mm/logging";
import { useEffect, useMemo, useState } from "react";

import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { FeatureHubGuard } from "@/featureFlags";
import CompleteThreshold from "@/overlay/components/CommonParts/Threshold/CompleteThreshold";
import { EnsembleMemberSelect } from "@/overlay/components/Ensamble";
import { InitDateField } from "@/overlay/components/LayerStackWindow/InitDateField";
import { OptionSeparatorModel } from "@/overlay/components/LayerStackWindow/PlotSerieOptions";
import { normalizeModelParamDependencies } from "@/overlay/components/LayerStackWindow/TableHelper";
import { hasCalibratedAvailable } from "@/utility/calibratedAvailabilityMap";
import {
  searchEnginesWeatherParams,
  stationModels_TEMPORAL,
  useSearchEngineReady,
  useWeatherParamModels,
  winterdienstStationModels_TEMPORAL,
} from "@/weather-parameters";
import type { WeatherTable, WeatherTableColumn } from "@/weatherTableView/types";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import type { ExactMatchResult, HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";
import { autofill, parameterToString } from "@mm/weather-parameter-utils";
import { useFlag } from "@openfeature/react-sdk";
import { cloneDeep } from "lodash";
import type { DraggableItemIdentifier } from "../../CommonParts/SortableList";
import { WeatherParameterForm } from "../../WeatherParameterForm";
import { FormField } from "../../form-field";
import { CalibratedToggle } from "../LayerFormElements";

const logger = Logger.fromFilename(__filename);

type WeatherTableDataFormProps = {
  weatherTable: WeatherTable;
  groupIdx: number;
  columnIdx: number;
  onChange: (weatherTable: WeatherTable) => void;
};

export function WeatherTableDataForm({ weatherTable, groupIdx, columnIdx, onChange }: WeatherTableDataFormProps) {
  const searchEngineReady = useSearchEngineReady();
  const weatherModels = useWeatherParamModels();
  const [models, setModels] = useState<ModelSchema[] | null>(null);
  const [selectedModelSchema, setSelectedModelSchema] = useState<ModelSchema | null>(null);

  const [currentParam, setCurrentParam] = useState<PartialWeatherParameter | null>(null);

  const column = useMemo(() => weatherTable.groups[groupIdx]?.columns[columnIdx], [weatherTable, groupIdx, columnIdx]);
  const { value: winterdienstStationsEnabled } = useFlag("winterdienst-stations", false);

  useEffect(() => {
    if (weatherModels) {
      setModels([
        ...weatherModels.filter((model) =>
          currentParam ? currentParam.parameter.available_models?.includes(model.identifier) : false,
        ),
        OptionSeparatorModel,
        ...stationModels_TEMPORAL,
        ...(winterdienstStationsEnabled ? winterdienstStationModels_TEMPORAL : []),
      ]);
    }
  }, [weatherModels, currentParam, winterdienstStationsEnabled]);

  useEffect(() => {
    if (models && column?.model) {
      const modelSchema = models.find((m) => m.identifier === column.model);
      setSelectedModelSchema(modelSchema ?? null);
    }
  }, [models, column?.model]);

  useEffect(() => {
    let isSubscribed = true;
    if (!currentParam && searchEngineReady) {
      if (!searchEnginesWeatherParams.standard) {
        return;
      }

      searchEnginesWeatherParams.standard
        .search(column?.parameterUnit)
        .then((response) => {
          if (isSubscribed) {
            if (response.results?.regular?.[0]) {
              const result: ExactMatchResult = response.results.regular[0];
              setCurrentParam(
                autofill({
                  parameter: result.layerGroup,
                  narrowed_selection: {
                    layerIdx: result.matchingLayers[0].layerIdx,
                    // biome-ignore lint/style/noNonNullAssertion: TODO this is becoming unacceptable
                    fields: result.matchingLayers[0].match.groups!,
                  },
                }),
              );
            } else {
              // TODO: Feedback to user, show a available parameter or hide the old Layer
              logger.error(`no exactMatchResult for term   ${column?.parameterUnit} errored out.`);
            }
          }
        })
        .catch((e) => {
          // TODO: errors here are most likely reoccuring. We should probably show an error in this case instead of logging.
          // However, any error here renders the application unusable... So, adding CI tests to prevent this is most likely
          // a better option than a nice error message.
          logger.error(`search for term probably close to ${column?.parameterUnit} errored out.`, e);
        });
    }
    return () => {
      isSubscribed = false;
    };
  }, [column?.parameterUnit, currentParam, searchEngineReady]);

  function updateWeatherParameter(param: HasLayerIdxNarrowing<PartialWeatherParameter>) {
    if (currentParam && searchEngineReady) {
      setCurrentParam(param);
      const parameter = parameterToString(param);
      // TODO: check if new Parameter has other Models then befor if so change the select of model, models options anyway needs to be updated
      if (parameter && parameter.missingFields.length === 0) {
        // changeProps({ parameter_unit: parameter });
        updateColumn({
          parameterUnit: parameter.formatted,
          title: column.title === column.parameterUnit ? parameter.formatted : column.title,
        });
      }
    }
  }

  function updateColumn(patch: Partial<WeatherTableColumn>) {
    const normalizedPatch = normalizeModelParamDependencies(column, patch, models);

    const updatedWeatherTable = cloneDeep(weatherTable);
    updatedWeatherTable.groups[groupIdx].columns[columnIdx] = {
      ...column,
      ...normalizedPatch,
    };

    onChange(updatedWeatherTable);
  }

  function moveThresholdIdx(dragStart: DraggableItemIdentifier, dropTarget: DraggableItemIdentifier) {
    if (!column.thresholds) {
      console.error("Threshold orders were swapped but no data is available");
      return;
    }

    const copyOfThresholds = [...column.thresholds];

    const fromIdx = dragStart.index;
    let toIdx = dropTarget.index;
    if (toIdx >= copyOfThresholds.length) {
      toIdx = copyOfThresholds.length - 1;
    }
    if (toIdx < 0) {
      toIdx = 0;
    }
    const targetThreshold = copyOfThresholds.splice(fromIdx, 1)[0];
    copyOfThresholds.splice(toIdx, 0, targetThreshold);

    updateColumn({
      thresholds: copyOfThresholds,
    });
  }

  return (
    <>
      {models && (
        <FormField label={t`Source`}>
          <ModelDropdown
            options={models?.map((model: any) => ({ id: model.identifier, title: `${model.name}` })) || []}
            value={column.model}
            onChange={(newModel) => {
              updateColumn({ model: newModel });
            }}
            placeholder={"Select a model"}
          />
        </FormField>
      )}
      {currentParam && (
        <>
          <WeatherParameterForm
            autofill={true}
            showExactParameter={true}
            parameter={currentParam}
            onChange={updateWeatherParameter}
            onSubmitParam={updateWeatherParameter}
          />
          {selectedModelSchema?.num_ensemble_members && (
            <EnsembleMemberSelect
              ensSelect={column?.ensSelect ?? ""}
              selectedModelSchema={selectedModelSchema}
              onChange={({ ens_select }) => updateColumn({ ensSelect: ens_select })}
              allowMultipleEnsSelect={false}
              custom={true}
            />
          )}
          {hasCalibratedAvailable(column?.parameterUnit, column?.model) && (
            <CalibratedToggle value={column.calibrated} change={(propsValue) => updateColumn(propsValue)} />
          )}
        </>
      )}
      <FeatureHubGuard flag="api_url_suffix">
        <InitDateField
          label={t`API URL suffix`}
          defaultValue={column.apiUrlSuffix ?? ""}
          onChange={(apiUrlSuffix) => updateColumn({ apiUrlSuffix })}
        />
      </FeatureHubGuard>
      <CompleteThreshold
        onMoveIndex={moveThresholdIdx}
        updateThresholds={(newThresholds) => updateColumn({ thresholds: newThresholds })}
        updateAdvancedThresholds={(newThresholds) => updateColumn({ advancedThresholds: newThresholds })}
        thresholds={column.thresholds}
        advancedThresholds={column.advancedThresholds}
        paramUnit={column?.parameterUnit}
      />
    </>
  );
}
